import { adminAxiosInstance } from "./adminAxiosInstance";

const supportedChannels = new Set(["push", "sms", "email"]);
const supportedHistoryChannels = new Set(["all", ...supportedChannels]);
const supportedHistoryAudiences = new Set(["registered_users", "guest_users", "farms"]);
const supportedHistorySegments = new Set(["all", "frequent_buyers", "inactive_buyers", "new_users", "new_farms", "verified_farms", "custom_selection"]);

const normalizePagination = (pagination = {}, pageSize = 10) => ({
  total_items: Number(pagination.total_items || 0),
  current_page: Number(pagination.current_page || 1),
  total_pages: Math.max(Number(pagination.total_pages || 1), 1),
  pageSize: Number(pagination.pageSize || pageSize),
});

const isoDate = (value) => {
  if (!value) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString().slice(0, 10);
};

const constrainEmailImages = (html) => {
  if (typeof DOMParser === "undefined" || !html) return html;
  const document = new DOMParser().parseFromString(html, "text/html");
  document.body.querySelectorAll("img").forEach((image) => {
    image.style.setProperty("display", "block");
    image.style.setProperty("width", "100%");
    image.style.setProperty("max-width", "100%");
    image.style.setProperty("height", "auto");
    image.style.setProperty("box-sizing", "border-box");
  });
  return document.body.innerHTML;
};

export const getMarketingBroadcastHistory = async ({ channel = "all", audience = "", segment = "all", dispatchedBy = "", startDate, endDate, page = 1, pageSize = 10 } = {}) => {
  if (!supportedHistoryChannels.has(channel)) throw new Error("Choose a valid broadcast history channel.");
  if (audience && !supportedHistoryAudiences.has(audience)) throw new Error("Choose a valid broadcast history audience.");
  if (!supportedHistorySegments.has(segment)) throw new Error("Choose a valid broadcast history segment.");

  const { data } = await adminAxiosInstance.get("/admin/internal/marketing/broadcast/history", {
    params: {
      channel,
      ...(audience ? { audience } : {}),
      segment,
      ...(dispatchedBy.trim() ? { dispatched_by: dispatchedBy.trim() } : {}),
      ...(startDate ? { startDate: isoDate(startDate) } : {}),
      ...(endDate ? { endDate: isoDate(endDate) } : {}),
      page,
      pageSize,
    },
  });
  if (!data?.status) throw new Error(data?.message || "Broadcast history could not be loaded.");

  return {
    data: Array.isArray(data.data) ? data.data : [],
    pagination: normalizePagination(data.pagination, pageSize),
  };
};

export const sendMarketingBroadcast = async ({ channel, audience, target = "segment", segment, days = 30, ids = [], title, subject, message }) => {
  if (!supportedChannels.has(channel)) throw new Error("Choose a valid broadcast channel.");
  if (target === "custom_selection" && !ids.length) throw new Error("Select at least one recipient before broadcasting.");

  const body = {
    audience,
    target,
    ...(target === "custom_selection" ? { ids } : { segment, days: Number(days) }),
    ...(channel === "push" ? { title } : {}),
    ...(channel === "email" ? { subject } : {}),
    message: channel === "email" ? constrainEmailImages(message) : message,
  };

  const { data } = await adminAxiosInstance.post(`/admin/internal/marketing/broadcast/${channel}`, body);
  if (!data?.status) throw new Error(data?.message || "The broadcast could not be processed.");

  return {
    status: true,
    message: data.message,
    summary: {
      total_targeted: Number(data.summary?.total_targeted || 0),
      sent_count: Number(data.summary?.sent_count || 0),
      failed_count: Number(data.summary?.failed_count || 0),
      skipped_count: Number(data.summary?.skipped_count || 0),
    },
  };
};
